import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';

import { OperationProfileService } from './services/operation-profile.service';
import {
  Operation,
  OperationProfile,
  OperationProfileRequest,
  OperationRequest,
  TemplatePullPreviewResponse,
  TemplatePullRequest,
  UpdateOperationProfileRequest,
} from './models/operation-profile.model';
import { ProfileDialogComponent, DialogMode } from './dialogs/profile-dialog.component';
import { OperationDialogComponent } from './dialogs/operation-dialog.component';
import { TemplatePullDialogComponent } from './dialogs/template-pull-dialog.component';
import { ToastService } from '../../shared/services/toast-service';
import { ModalService } from '../../shared/components/modal/modal.service';
import { LanguageService } from '../../core/i18n/language.service';
import { Grid } from '../../shared/components/grid/grid';
import { GridPage } from '../../shared/components/grid/grid-page';
import { GridCellDirective } from '../../shared/components/grid/directives/grid-cell.directive';
import { GridDetailDirective } from '../../shared/components/grid/directives/grid-detail.directive';
import { GridColumn, GridSort, gridColumns } from '../../shared/components/grid/models/grid.model';
import { Icon } from '../../shared/components/icon/icon';
import { ScheduleStrip, ScheduleBand } from '../../shared/components/schedule-strip/schedule-strip';
import { IlluminationBar } from '../../shared/components/illumination-bar/illumination-bar';
import { PowerThresholdBar } from '../../shared/components/power-threshold-bar/power-threshold-bar';
import { LabelBadge, LabelBadgeDot } from '../../shared/components/label-badge/label-badge';
import { Badge } from '../../shared/components/badge/badge';
import { Button } from '../../shared/components/button/button';

/** Detail-panel tab within the profile inspector. */
type DetailTab = 'overview' | 'operations' | 'displays';

@Component({
  selector: 'operations',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslocoDirective,
    Icon,
    ScheduleStrip,
    IlluminationBar,
    PowerThresholdBar,
    Grid,
    GridPage,
    GridCellDirective,
    GridDetailDirective,
    ProfileDialogComponent,
    OperationDialogComponent,
    TemplatePullDialogComponent,
    LabelBadge,
    Badge,
    Button,
  ],
  templateUrl: './operations.component.html',
})
export class OperationsComponent implements OnInit {
  private readonly operationProfileService = inject(OperationProfileService);
  private readonly toastService = inject(ToastService);
  private readonly transloco = inject(TranslocoService);
  private readonly languageService = inject(LanguageService);
  private readonly modal = inject(ModalService);
  private readonly destroyRef = inject(DestroyRef);

  readonly profiles = signal<OperationProfile[]>([]);
  readonly page = signal(0);
  readonly size = signal(20);
  readonly totalElements = signal(0);
  readonly totalPages = signal(0);
  readonly sort = signal<GridSort>({ sortBy: 'created', sortDirection: 'DESC' });
  readonly isLoading = signal(false);

  /** Master/detail link: the profile shown in the inspector. */
  readonly selectedProfile = signal<OperationProfile | null>(null);
  /** Operations for the selected profile (detail "Operations" tab). */
  readonly operations = signal<Operation[]>([]);
  readonly detailTab = signal<DetailTab>('overview');

  // Dialogs + delete confirmation
  readonly isProfileDialogOpen = signal(false);
  /** Save request in flight — freezes the profile wizard (its busy input). */
  readonly isProfileSaving = signal(false);
  readonly isOperationDialogOpen = signal(false);
  readonly dialogMode = signal<DialogMode>('add');
  readonly editingProfile = signal<OperationProfile | null>(null);
  readonly editingOperation = signal<Operation | null>(null);

  // Template-pull dialog. Opened from the detail pane when a profile has a
  // newer template version. We hold the fetched preview + the profile it's for,
  // and freeze the dialog's confirm while the apply request is in flight.
  readonly isTemplatePullOpen = signal(false);
  readonly templatePullPreview = signal<TemplatePullPreviewResponse | null>(null);
  readonly templatePullProfile = signal<OperationProfile | null>(null);
  readonly isTemplatePullLoading = signal(false);
  readonly isTemplatePullApplying = signal(false);

  readonly profileId = (p: OperationProfile) => String(p.id);
  readonly asProfile = (row: unknown): OperationProfile => row as OperationProfile;

  /** Columns as a computed so labels re-translate on a language switch. The
   *  master is narrow (detail pane is open), so these overflow into the grid's
   *  horizontal scroll rather than cramming into one cell. */
  readonly columns = computed<GridColumn<OperationProfile>[]>(() => {
    this.languageService.current();
    const t = (k: string) => this.transloco.translate(`operations.columns.${k}`);
    return gridColumns<OperationProfile>([
      { key: 'operationProfileId', label: t('profile'), type: 'template', template: 'profile', sortable: true, alwaysVisible: true, width: 210 },
      { key: 'status', label: t('status'), type: 'template', template: 'status', sortable: false, width: 110 },
      { key: 'powerMode', label: t('powerMode'), type: 'template', template: 'powerMode', sortable: true, width: 110 },
      { key: 'schedule', label: t('schedule'), type: 'template', template: 'schedule', sortable: false, width: 190 },
      { key: 'render', label: t('render'), type: 'text', sortable: false, width: 90, value: (p) => this.rowRender(p) },
      { key: 'connection', label: t('connection'), type: 'text', sortable: false, width: 100, value: (p) => this.rowConnection(p) },
      { key: 'templateStatus', label: t('templateStatus'), type: 'template', template: 'templateStatus', sortable: false, width: 160 },
      { key: 'week', label: t('week'), type: 'template', template: 'week', sortable: false, width: 170 },
    ]);
  });

  ngOnInit(): void {
    this.loadProfiles();
  }

  private loadProfiles(): void {
    const { sortBy, sortDirection } = this.sort();
    this.isLoading.set(true);
    this.operationProfileService
      .getProfiles(this.page(), this.size(), sortBy, sortDirection)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.profiles.set(response.data);
          this.page.set(response.page);
          this.size.set(response.size);
          this.totalElements.set(response.totalElements);
          this.totalPages.set(response.totalPages);
          this.ensureSelection(response.data);
          this.isLoading.set(false);
        },
        error: () => {
          this.toastService.show('Failed to load operation profiles', 'error');
          this.isLoading.set(false);
        },
      });
  }

  private loadOperations(): void {
    const profile = this.selectedProfile();
    if (!profile) return;
    this.operationProfileService
      .getOperationsByProfileId(profile.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => this.operations.set(response.data),
        error: () => this.toastService.show('Failed to load operations', 'error'),
      });
  }

  onSortChange(sort: GridSort): void {
    this.sort.set(sort);
    this.page.set(0);
    this.loadProfiles();
  }

  onPageChange(page: number): void {
    this.page.set(page);
    this.loadProfiles();
  }

  onPageSizeChange(size: number): void {
    this.size.set(size);
    this.page.set(0);
    this.loadProfiles();
  }

  /** Row click selects the profile → populates the inspector + loads operations. */
  onRowClick(profile: OperationProfile): void {
    this.selectedProfile.set(profile);
    this.detailTab.set('overview');
    this.operations.set([]);
    this.loadOperations();
  }

  /**
   * Keep the detail pane populated: after a load, select the first profile when
   * there's no valid selection. "Valid" means the currently-selected profile is
   * still present in the freshly-loaded page — otherwise (deleted, or paged
   * away) we fall back to the first row so the split detail is never empty.
   */
  private ensureSelection(profiles: OperationProfile[]): void {
    const current = this.selectedProfile();
    const stillPresent = current != null && profiles.some((p) => p.id === current.id);
    if (stillPresent || profiles.length === 0) return;
    this.onRowClick(profiles[0]);
  }

  setTab(tab: DetailTab): void {
    this.detailTab.set(tab);
  }

  // ── Display helpers (mirror the design's formatting) ──────────────────

  /** Per-operation band colours (one per op, cycled) for the coverage strip. */
  private static readonly OP_COLORS = [
    'var(--color-brand)',
    'var(--color-warning)',
    'var(--color-success)',
    'var(--color-caution)',
  ];

  /** Human day-set label: Every day / Mon–Fri / Weekends / explicit list.
   *  Handles both full ('MONDAY') and short ('MON') day codes. */
  daysLabel(days: string[]): string {
    const t = (k: string) => this.transloco.translate(`operations.days.${k}`);
    if (!days?.length) return this.transloco.translate('operations.na');
    const norm = days.map((d) => d.slice(0, 3).toUpperCase());
    if (norm.length === 7) return t('everyDay');
    const weekdays = ['MON', 'TUE', 'WED', 'THU', 'FRI'];
    const weekend = ['SAT', 'SUN'];
    if (norm.length === 5 && weekdays.every((d) => norm.includes(d))) return t('weekdays');
    if (norm.length === 2 && weekend.every((d) => norm.includes(d))) return t('weekends');
    return norm.map((d) => d[0] + d.slice(1).toLowerCase()).join(', ');
  }

  /** Active-window label, collapsing a full day to "24 h". */
  windowLabel(op: { startTime: string | null; endTime: string | null }): string {
    const { startTime, endTime } = op;
    if (!startTime || !endTime) return this.transloco.translate('operations.na');
    if (startTime === '00:00' && (endTime === '23:59' || endTime === '24:00')) {
      return this.transloco.translate('operations.window.allDay');
    }
    return `${startTime}–${endTime}`;
  }

  /** Date-range label, collapsing the "far future" sentinel end to "no end". */
  dateRangeLabel(op: { startDate: string | null; endDate: string | null }): string {
    if (!op.startDate) return this.transloco.translate('operations.na');
    const noEnd = this.transloco.translate('operations.settings.noEnd');
    const end = !op.endDate || op.endDate.startsWith('9999') ? noEnd : op.endDate;
    return `${op.startDate} → ${end}`;
  }

  /** One-line schedule summary for an operation: "Mon–Fri · 05:00–23:00 · 2026-01-01 → no end". */
  operationScheduleLabel(op: Operation): string {
    return `${this.daysLabel(op.days)} · ${this.windowLabel(op)} · ${this.dateRangeLabel(op)}`;
  }

  // ── Master-row "effective" settings (the operation whose settings apply) ───
  // We surface the profile's first operation; profiles with none fall back to
  // profile-level defaults. (No time-of-day matching — a single representative
  // operation per row, per the master-list design.)

  /** The representative operation for a profile's row, or null if it has none. */
  private effectiveOperation(profile: OperationProfile): Operation | null {
    return profile.operations?.[0] ?? null;
  }

  /** Whether the row is showing a real operation vs. falling back to defaults. */
  hasEffectiveOperation(profile: OperationProfile): boolean {
    return this.effectiveOperation(profile) !== null;
  }

  /** "Mon–Fri · 05:00–23:00" for the row's operation, or defaults note. */
  rowScheduleLabel(profile: OperationProfile): string {
    const op = this.effectiveOperation(profile);
    if (!op) return this.transloco.translate('operations.row.defaults');
    return `${this.daysLabel(op.days)} · ${this.windowLabel(op)}`;
  }

  /** Effective render interval: op's if present, else the profile default. */
  rowRender(profile: OperationProfile): string {
    return this.formatInterval(this.effectiveOperation(profile)?.renderInterval ?? profile.renderInterval);
  }
  rowConnection(profile: OperationProfile): string {
    return this.formatInterval(this.effectiveOperation(profile)?.connectionInterval ?? profile.connectionInterval);
  }

  /** Active/Sleep/Shutdown flag for the row's operation. */
  rowFlag(profile: OperationProfile): 'active' | 'sleep' | 'shutdown' | null {
    const op = this.effectiveOperation(profile);
    if (!op) return null;
    if (op.shutdown) return 'shutdown';
    if (op.sleep) return 'sleep';
    return 'active';
  }

  /** Status-dot colour for an active/sleep/shutdown flag. */
  flagDot(flag: 'active' | 'sleep' | 'shutdown' | null): 'green' | 'blue' | 'red' | 'gray' {
    switch (flag) {
      case 'active':
        return 'green';
      case 'sleep':
        return 'blue';
      case 'shutdown':
        return 'red';
      default:
        return 'gray';
    }
  }

  /**
   * Template-provenance state for the row's "Template status" cell:
   * - 'update'   → derived from a template with a newer version to pull
   * - 'current'  → derived and already on the template's latest version
   * - 'standalone' → hand-made profile, not forked from any template
   */
  templateState(profile: OperationProfile): 'update' | 'current' | 'standalone' {
    if (!profile.fromTemplate) return 'standalone';
    return profile.updateAvailable ? 'update' : 'current';
  }

  // ── Template pull (review + apply a newer template version) ────────────

  /** Open the pull dialog for a profile: fetch the preview, then show it. */
  openTemplatePull(profile: OperationProfile): void {
    this.templatePullProfile.set(profile);
    this.templatePullPreview.set(null);
    this.isTemplatePullLoading.set(true);
    this.operationProfileService
      .previewTemplatePull(profile.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.isTemplatePullLoading.set(false);
          this.templatePullPreview.set(response.data);
          this.isTemplatePullOpen.set(true);
        },
        error: () => {
          this.isTemplatePullLoading.set(false);
          this.toastService.show(
            this.transloco.translate('operations.templatePull.previewFailed'),
            'error',
          );
        },
      });
  }

  closeTemplatePull(): void {
    this.isTemplatePullOpen.set(false);
    this.templatePullPreview.set(null);
    this.templatePullProfile.set(null);
  }

  /** Apply the operator's resolved pull. On a stale-version 409 the template
   *  advanced mid-review, so we silently re-preview and reopen. */
  applyTemplatePull(request: TemplatePullRequest): void {
    const profile = this.templatePullProfile();
    if (!profile) return;
    this.isTemplatePullApplying.set(true);
    this.operationProfileService
      .applyTemplatePull(profile.id, request)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.isTemplatePullApplying.set(false);
          this.closeTemplatePull();
          this.toastService.show(
            this.transloco.translate('operations.templatePull.applied'),
            'success',
          );
          // Apply replaces operations and re-stamps the version — reflect the
          // returned profile in the list + inspector, then refresh operations.
          this.profiles.update((list) =>
            list.map((p) => (p.id === response.data.id ? response.data : p)),
          );
          if (this.selectedProfile()?.id === response.data.id) {
            this.selectedProfile.set(response.data);
            this.loadOperations();
          }
        },
        error: (err) => {
          this.isTemplatePullApplying.set(false);
          if (err?.status === 409 && this.isStaleTemplateError(err)) {
            // Resolution is stale — re-run the preview against the new version.
            this.toastService.show(
              this.transloco.translate('operations.templatePull.staleRetry'),
              'info',
            );
            this.isTemplatePullOpen.set(false);
            this.openTemplatePull(profile);
            return;
          }
          this.toastService.show(
            this.transloco.translate('operations.templatePull.applyFailed'),
            'error',
          );
        },
      });
  }

  /** The 409 that means "template moved since preview" — the body message names
   *  the version drift. Other 409s (not a copy, already latest) are terminal. */
  private isStaleTemplateError(err: unknown): boolean {
    const message = (err as { error?: { message?: string } })?.error?.message ?? '';
    return /version|moved|current/i.test(message);
  }

  /** Status-dot colour for the power mode (design: Main = blue, others = yellow). */
  powerModeDot(mode: string): 'blue' | 'yellow' {
    return this.powerModeColor(mode) === 'blue' ? 'blue' : 'yellow';
  }

  /** LabelBadge dot colour for a battery-status pill. */
  batteryStatusDot(status: string): LabelBadgeDot | null {
    switch (this.batteryStatusColor(status)) {
      case 'green':
        return 'green';
      case 'orange':
        return 'orange';
      case 'red':
        return 'red';
      default:
        return null;
    }
  }

  /** Format an interval in seconds as "45 s" / "2 min". */
  formatInterval(seconds: number | null | undefined): string {
    if (seconds == null) return '—';
    if (seconds < 60) return `${seconds} s`;
    const m = seconds / 60;
    return `${Number.isInteger(m) ? m : m.toFixed(1)} min`;
  }

  operationsCount(profile: OperationProfile): number {
    return profile.operations?.length ?? 0;
  }

  illuminationOf(op: Operation): number {
    return op.illumination ?? 0;
  }

  /** Colour assigned to an operation by its position, for dots + coverage bands. */
  operationColor(index: number): string {
    return OperationsComponent.OP_COLORS[index % OperationsComponent.OP_COLORS.length];
  }

  /** Coverage bands for the Power-settings weekly strip — one per operation. */
  coverageBands(): ScheduleBand[] {
    return this.bandsFrom(this.operations());
  }

  /** Coverage bands from an arbitrary operation list (used by the master rows,
   *  which carry their own `operations` from the list payload). */
  profileBands(profile: OperationProfile): ScheduleBand[] {
    return this.bandsFrom(profile.operations ?? []);
  }

  private bandsFrom(ops: Operation[]): ScheduleBand[] {
    return ops.map((op, i) => ({
      days: op.days,
      startTime: op.startTime,
      endTime: op.endTime,
      color: this.operationColor(i),
      sleep: op.sleep,
    }));
  }

  // ── Power-mode / battery-status pill colours (semantic tokens) ─────────

  /** Power-mode pill colour. Real modes: MAIN (blue) / BATTERY (purple→neutral). */
  powerModeColor(mode: string): 'blue' | 'neutral' {
    return mode?.toUpperCase() === 'MAIN' ? 'blue' : 'neutral';
  }

  /** Battery-status pill colour by status code. */
  batteryStatusColor(status: string): 'green' | 'orange' | 'red' | 'neutral' {
    switch (status?.toUpperCase()) {
      case 'NORMAL':
        return 'green';
      case 'LOW_POWER':
        return 'orange';
      case 'NO_POWER':
        return 'red';
      default:
        return 'neutral';
    }
  }

  /** Humanise a battery status code: 'LOW_POWER' → 'Low power'. */
  batteryStatusLabel(status: string): string {
    return status
      .split('_')
      .map((w) => w[0] + w.slice(1).toLowerCase())
      .join(' ');
  }

  // ── Override detection (op value differs from profile default) ─────────

  /** The profile currently shown in the detail pane. */
  private currentProfile(): OperationProfile | null {
    return this.selectedProfile();
  }

  /**
   * A field is an "override" only when BOTH the operation's value and the
   * profile default exist and differ. If either side is absent there's no
   * baseline to differ from, so it's not an override (avoids false positives
   * when the backend hasn't populated a field — e.g. volume/aux today).
   */
  private isOverride(opValue: unknown, defaultValue: unknown): boolean {
    if (opValue == null || defaultValue == null) return false;
    return opValue !== defaultValue;
  }

  isRenderOverride(op: Operation): boolean {
    return this.isOverride(op.renderInterval, this.currentProfile()?.renderInterval);
  }
  isConnectionOverride(op: Operation): boolean {
    return this.isOverride(op.connectionInterval, this.currentProfile()?.connectionInterval);
  }
  isVolumeOverride(op: Operation): boolean {
    return this.isOverride(op.volume, this.currentProfile()?.volume);
  }
  isAuxOverride(op: Operation): boolean {
    return this.isOverride(op.auxOutput, this.currentProfile()?.auxOutput);
  }

  volumeLabel(v: number | undefined): string {
    return v === undefined ? '—' : `${v}%`;
  }
  auxLabel(on: boolean | undefined): string {
    if (on === undefined) return '—';
    return on ? this.transloco.translate('operations.on') : this.transloco.translate('operations.off');
  }

  // ── Profile CRUD + dialogs ────────────────────────────────────────────

  openAddProfileDialog(): void {
    this.editingProfile.set(null);
    this.dialogMode.set('add');
    this.isProfileDialogOpen.set(true);
  }

  editSelectedProfile(): void {
    const profile = this.selectedProfile();
    if (!profile) return;
    this.editingProfile.set(profile);
    this.dialogMode.set('edit');
    this.isProfileDialogOpen.set(true);
  }

  async deleteSelectedProfile(): Promise<void> {
    const profile = this.selectedProfile();
    if (!profile) return;
    const t = (k: string, params?: Record<string, unknown>) =>
      this.transloco.translate(`operations.deleteConfirm.${k}`, params);
    const confirmed = await this.modal.confirm({
      kind: 'destructive',
      destructive: true,
      title: t('title'),
      message: t('body', { name: profile.operationProfileId }),
      confirmLabel: t('confirm'),
      confirmWord: profile.operationProfileId,
    });
    if (confirmed) this.deleteProfile(profile);
  }

  closeProfileDialog(): void {
    this.isProfileDialogOpen.set(false);
    this.editingProfile.set(null);
  }

  handleProfileSave(request: OperationProfileRequest): void {
    const profile = this.editingProfile();
    this.isProfileSaving.set(true);
    if (this.dialogMode() === 'edit' && profile) {
      this.operationProfileService
        .updateProfile(profile.id, request as UpdateOperationProfileRequest)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (response) => {
            this.isProfileSaving.set(false);
            this.toastService.show('Operation Profile updated successfully', 'success');
            this.closeProfileDialog();
            // The endpoint returns the updated profile — apply it directly
            // instead of refetching the list, which isn't guaranteed to be
            // immediately consistent with the write that just happened (see
            // the same fix on the devices list).
            this.profiles.update((list) =>
              list.map((p) => (p.id === response.data.id ? response.data : p)),
            );
            if (this.selectedProfile()?.id === profile.id) {
              this.selectedProfile.set(response.data);
            }
          },
          error: () => {
            this.isProfileSaving.set(false);
            this.toastService.show('Failed to update operation profile', 'error');
          },
        });
    } else {
      this.operationProfileService
        .createProfile({ ...(request as UpdateOperationProfileRequest), operations: [] })
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (response) => {
            this.isProfileSaving.set(false);
            this.toastService.show('Operation Profile created successfully', 'success');
            this.closeProfileDialog();
            this.profiles.update((list) => [response.data, ...list]);
            this.totalElements.update((n) => n + 1);
          },
          error: () => {
            this.isProfileSaving.set(false);
            this.toastService.show('Failed to create operation profile', 'error');
          },
        });
    }
  }

  private deleteProfile(profile: OperationProfile): void {
    this.operationProfileService
      .deleteProfile(profile.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.toastService.show('Operation Profile deleted successfully', 'success');
          if (this.selectedProfile()?.id === profile.id) {
            this.selectedProfile.set(null);
            this.operations.set([]);
          }
          this.loadProfiles();
        },
        error: () => this.toastService.show('Failed to delete operation profile', 'error'),
      });
  }

  // ── Operation CRUD + dialog ───────────────────────────────────────────

  openAddOperationDialog(): void {
    this.editingOperation.set(null);
    this.dialogMode.set('add');
    this.isOperationDialogOpen.set(true);
  }

  editOperation(operation: Operation): void {
    this.editingOperation.set(operation);
    this.dialogMode.set('edit');
    this.isOperationDialogOpen.set(true);
  }

  closeOperationDialog(): void {
    this.isOperationDialogOpen.set(false);
    this.editingOperation.set(null);
  }

  handleOperationSave(request: OperationRequest): void {
    const operation = this.editingOperation();
    if (this.dialogMode() === 'edit' && operation) {
      this.operationProfileService
        .updateOperation(operation.id, request)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => {
            this.toastService.show('Operation updated successfully', 'success');
            this.closeOperationDialog();
            this.loadOperations();
          },
          error: () => this.toastService.show('Failed to update operation', 'error'),
        });
    } else {
      const profileId = this.selectedProfile()?.id;
      if (!profileId) return;
      this.operationProfileService
        .createOperation(profileId, request)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => {
            this.toastService.show('Operation created successfully', 'success');
            this.closeOperationDialog();
            this.loadOperations();
          },
          error: () => this.toastService.show('Failed to create operation', 'error'),
        });
    }
  }

  deleteOperation(operation: Operation): void {
    this.operationProfileService
      .deleteOperation(operation.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.toastService.show('Operation deleted successfully', 'success');
          this.loadOperations();
        },
        error: () => this.toastService.show('Failed to delete operation', 'error'),
      });
  }
}
